import { Icon } from "@/ui-kit/NovaIcon/NovaIcon.vue";
import { ExperienceRevision } from "../types/revision";
import { MappedCategory, SidebarSections } from "@/types/DocumentSidebar";
import { mapSidebarSections } from "@/features/experience-shared/utils/map-sidebar-sections";
import TranslationSettingsForm from "@/features/experience-translation/forms/TranslationSettingsForm.vue";
import { AvailableLanguage } from "@/types/Language";
import TranslationLocationForm from "@/features/experience-translation/forms/TranslationLocationForm.vue";
import TranslationContentGenerationForm from "@/features/experience-translation/forms/TranslationContentGenerationForm.vue";
import TranslationCustomerInformationForm from "@/features/experience-translation/forms/TranslationCustomerInformationForm.vue";
import RevisionTranslationOperationalForm from "@/features/experience-revision/components/RevisionTranslationOperationalForm.vue";
import { translationHighlightArraySchema } from "@/features/experience-translation/schemas";
import { useFeatureFlag } from "@/features/experience-shared/composables/useFeatureFlag";
import RevisionTranslationOptionNamesForm from "@/features/experience-revision/components/RevisionTranslationOptionNamesForm.vue";

export function getTranslationForms(
  lang: string,
  values: ExperienceRevision | null,
  curationValues: ExperienceRevision | null
): Record<string, { is: unknown; props: unknown }> {
  const language = lang as AvailableLanguage;

  const forms: Record<string, { is: unknown; props: unknown }> = {
    settings: {
      is: TranslationSettingsForm,
      props: {
        curationValues: {
          title: curationValues?.title,
          seo_title: curationValues?.seoTitle,
        },
        readonly: true,
        initialValues: {
          title: values?.title,
          seo_title: values?.seoTitle,
        },
        language: language,
      },
    },
    location: {
      is: TranslationLocationForm,
      props: {
        curationValues: {
          meeting_point_details: curationValues?.meetingPointDetails,
        },
        hasDiff: false,
        readonly: true,
        initialValues: {
          meeting_point_details: values?.meetingPointDetails,
        },
        language,
      },
    },
    "content-segmentation": {
      is: TranslationContentGenerationForm,
      props: {
        isRevision: true,
        curationValues: {
          text1: curationValues?.description,
          seo_description: curationValues?.seoDescription,
          text2: curationValues?.additionalDescription,
          custom_highlights: useDefaultValue(curationValues?.highlights?.custom),
          custom_included: useDefaultValue(curationValues?.included?.custom),
          custom_non_included: useDefaultValue(curationValues?.nonIncluded?.custom),
          custom_important_information: useDefaultValue(curationValues?.importantInformation?.custom),
          premade_highlights: useDefaultValue(curationValues?.highlights?.premade),
          premade_included: useDefaultValue(curationValues?.included?.premade),
          premade_non_included: useDefaultValue(curationValues?.nonIncluded?.premade),
          premade_important_information: useDefaultValue(curationValues?.importantInformation?.premade),
        },
        hasDiff: false,
        readonly: true,
        initialValues: {
          text1: values?.description,
          seo_description: values?.seoDescription,
          text2: values?.additionalDescription,
          custom_highlights: useDefaultValue(values?.highlights?.custom),
          custom_included: useDefaultValue(values?.included?.custom),
          custom_non_included: useDefaultValue(values?.nonIncluded?.custom),
          custom_important_information: useDefaultValue(values?.importantInformation?.custom),
          premade_highlights: useDefaultValue(values?.highlights?.premade),
          premade_included: useDefaultValue(values?.included?.premade),
          premade_non_included: useDefaultValue(values?.nonIncluded?.premade),
          premade_important_information: useDefaultValue(values?.importantInformation?.premade),
        },
        language,
      },
    },
    "customer-info": {
      is: TranslationCustomerInformationForm,
      props: {
        curationValues: {
          info_voucher: curationValues?.infoVoucher,
        },
        hasDiff: false,
        readonly: true,
        initialValues: {
          info_voucher: values?.infoVoucher,
        },
        language,
      },
    },
    "option-names": {
      is: RevisionTranslationOptionNamesForm,
      props: {
        values,
        curationValues,
        options: null,
        requiredFields: [],
        flow: "translation",
      },
    },
  };

  // Dynamic operational sections — one form per operational translation
  if (values?.operationalTranslations?.length) {
    for (const translation of values.operationalTranslations) {
      forms[`operational-${translation.operational_id}`] = {
        is: RevisionTranslationOperationalForm,
        props: {
          values,
          curationValues,
          options: null,
          requiredFields: [],
          flow: "translation",
        },
      };
    }
  }

  return forms;
}

export function getTranslationSections(
  baseUrl: string,
  language: string,
  values: ExperienceRevision
): Record<string, MappedCategory> {
  const sections: SidebarSections = {
    settings: {
      url: `${baseUrl}/${language}/settings`,
      icon: "settings" as Icon,
      fields: [
        {
          id: "title",
          required: true,
          filled: !!values?.title,
        },
        {
          id: "seo_title",
          required: false,
          filled: !!values?.seoTitle,
        },
      ],
    },
    location: {
      url: `${baseUrl}/${language}/location`,
      icon: "location" as Icon,
      fields: [
        {
          id: "meeting_point_details",
          required: false,
          filled: !!values?.meetingPointDetails,
        },
      ],
    },
    "content-segmentation": {
      url: `${baseUrl}/${language}/content-segmentation`,
      icon: "content-generator",
      fields: [
        {
          id: "description",
          required: true,
          filled: !!values.description,
        },
        {
          id: "seo_description",
          required: true,
          filled: !!values.seoDescription,
        },
        {
          id: "additional_description",
          required: false,
          filled: !!values.additionalDescription,
          hide: !useFeatureFlag("showAdditionalDescription"),
        },
        {
          id: "highlights",
          required: true,
          filled: translationHighlightArraySchema.safeParse(values.highlights?.custom).success,
        },
        {
          id: "included",
          required: true,
          filled: translationHighlightArraySchema.safeParse(values.included?.custom).success,
        },
        {
          id: "non_included",
          required: false,
          filled: translationHighlightArraySchema.safeParse(values.nonIncluded?.custom).success,
        },
        {
          id: "important_information",
          required: false,
          filled: translationHighlightArraySchema.safeParse(values.importantInformation?.custom).success,
        },
      ],
    },
    "customer-info": {
      url: `${baseUrl}/${language}/customer-info`,
      icon: "customer-info",
      fields: [
        {
          id: "info_voucher",
          required: false,
          filled: !!values.infoVoucher,
        },
      ],
    },
    "option-names": {
      url: `${baseUrl}/${language}/option-names`,
      icon: "options" as Icon,
      noDropdown: true,
      hide: !values?.optionNames?.length,
      fields: [
        {
          id: "option-names",
          required: false,
          filled: !!values?.optionNames?.length,
        },
      ],
    },
    ...getOperationalSections(baseUrl, language, values),
  };

  return mapSidebarSections(sections);
}

function getOperationalSections(baseUrl: string, language: string, values?: ExperienceRevision): SidebarSections {
  const sections: SidebarSections = {};

  if (!values?.operationalTranslations?.length) {
    return sections;
  }

  for (const translation of values.operationalTranslations) {
    const sectionKey = `operational-${translation.operational_id}`;
    const relatedOptionNames = values.optionNames?.filter((opt) => opt.operational_id === translation.operational_id);

    sections[sectionKey] = {
      url: `${baseUrl}/${language}/operational-${translation.operational_id}`,
      label: translation.code,
      icon: "calendar" as Icon,
      noDropdown: true,
      fields: [
        {
          id: `operational-title-${translation.operational_id}`,
          required: false,
          filled: !!translation.name,
        },
        {
          id: `operational-option-names-${translation.operational_id}`,
          required: false,
          filled: !!relatedOptionNames?.length,
        },
      ],
    };
  }

  return sections;
}

// Sonar complains that passing a default value inline for a nullable value is "tOo CoMpLiCaTeD"
function useDefaultValue<T>(value: T, defaultValue = []): never[] | NonNullable<T> {
  return value ?? defaultValue;
}
